import { useDispatch, useSelector } from 'react-redux';
import { formatDateOrEmpty } from '@helpers/dates';
import useMediaQuery from '@mui/material/useMediaQuery';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import { InternalLink } from '@components/Link';
import { AccIntegrationName, ProfileType, ProfileName } from '@constants/integrations';
import { CONTACT_ACTIVITY_TYPE, PAYMENT_COMPLETED_EVENT } from '@constants/contacts';
import { findEventTrigger } from '@constants/automations';
import { RoutePath } from '@constants/routes';
import { SurveyResponseSource } from '@constants/feedbacks';
import { SurveysIcon, ReviewsIcon } from 'assets/images/sidebar';
import { UserIcon, CustomEventIcon, CouponRedeemedIcon } from 'assets/images/icons';
import { CoverManagerIcon, SquareIcon, VTEXIcon } from 'assets/images/integrations';
import { useAccountPeople, usePermissions, useTranslation } from '@hooks';
import { getChannelIcon } from '@helpers/channels';
import { pretifyObject } from '@helpers/pretify-object';
import { formatMoney } from '@helpers/formatNumbers';
import { contactsActions } from '@actions';
import PaymentDetails from './PaymentDetails';
import { ActivityWrapper, IconWrapper, CustomEventDataRaw } from './styles';

const ACTIVITY_MODULE = {
    [CONTACT_ACTIVITY_TYPE.MessageSent]: 'messaging',
    [CONTACT_ACTIVITY_TYPE.SurveyResponded]: 'surveys',
    [CONTACT_ACTIVITY_TYPE.SurveySent]: 'surveys',
    [CONTACT_ACTIVITY_TYPE.SurveyReminderSent]: 'surveys',
    [CONTACT_ACTIVITY_TYPE.ConversationStarted]: 'messaging',
    [CONTACT_ACTIVITY_TYPE.ConversationClosed]: 'messaging',
    [CONTACT_ACTIVITY_TYPE.ConversationReopened]: 'messaging',
    [CONTACT_ACTIVITY_TYPE.ReviewLeft]: 'reviews',
    [CONTACT_ACTIVITY_TYPE.CouponRedeemed]: 'coupons',
    [CONTACT_ACTIVITY_TYPE.FormSubmitted]: 'microsites',
};

const Activity = ({ contact, isSmallScreen, resizable, setActiveConversation = null }) => {
    const { t } = useTranslation();
    const { getPersonName } = useAccountPeople();
    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const dispatch = useDispatch();
    const language = useSelector(state => state.auth.user?.language);
    const { hasAccess } = usePermissions();

    const onClickInboxActivity = () => {
        if (isMobile) {
            dispatch(contactsActions.setContactOpen(false));
            // INFO: if mobile, close contact drawer and inbox to return to the list of conversations
            if (setActiveConversation) {
                setActiveConversation(null);
            }
        }
    };

    const getIntegrationEventIcon = integrationType => {
        switch (integrationType) {
            case ProfileName.CoverManager:
                return <CoverManagerIcon height={16} width={16} style={{ transform: 'translateY(0px)' }} />;
            case ProfileName.VTEX:
                return <VTEXIcon height={17} width={17} style={{ transform: 'translateY(0px)' }} />;
            case AccIntegrationName.Square:
                return <SquareIcon height={16} width={16} style={{ transform: 'translateY(0px)' }} />;
            default:
                return null;
        }
    };

    const getActorName = item => getPersonName(item?.userId) || item?.userName;

    const getCustomEventTitle = item => {
        if (item?.customEventType !== PAYMENT_COMPLETED_EVENT) {
            const eventTrigger = findEventTrigger(item?.customEventType);
            return eventTrigger
                ? t(`AutomationsPage.triggers.${eventTrigger.key}`)
                : `${t('common.customEvent')}: ${item?.customEventType}`;
        }
        const data = JSON.parse(item?.customEventDataRaw || '{}');
        const amount = Number.isFinite(data.amount) ? formatMoney(data.amount, data.currency) : null;
        return [t('ContactsPage.activityPaymentCompleted'), amount].filter(Boolean).join(' · ');
    };

    const getActivityIcon = item => {
        switch (item?.type) {
            case CONTACT_ACTIVITY_TYPE.SurveyResponded:
                return (
                    <IconWrapper>
                        <SurveysIcon />
                    </IconWrapper>
                );
            case CONTACT_ACTIVITY_TYPE.ReviewLeft:
                return (
                    <IconWrapper>
                        <ReviewsIcon />
                    </IconWrapper>
                );
            case CONTACT_ACTIVITY_TYPE.ContactCreated:
                return (
                    <IconWrapper>
                        <UserIcon />
                    </IconWrapper>
                );
            case CONTACT_ACTIVITY_TYPE.Integration:
                return getIntegrationEventIcon(item?.integrationType);
            case CONTACT_ACTIVITY_TYPE.CustomEvent:
                return (
                    getIntegrationEventIcon(item?.integrationType) || (
                        <CustomEventIcon style={{ transform: 'translateY(0px)' }} />
                    )
                );
            case CONTACT_ACTIVITY_TYPE.CouponRedeemed:
                return <CouponRedeemedIcon style={{ transform: 'translateY(0px)' }} />;
            case CONTACT_ACTIVITY_TYPE.FormSubmitted:
                return (
                    <IconWrapper>
                        <FactCheckRoundedIcon sx={{ fontSize: 20 }} />
                    </IconWrapper>
                );
            case CONTACT_ACTIVITY_TYPE.AddedToSegment:
                return (
                    <IconWrapper>
                        <LabelRoundedIcon sx={{ fontSize: 20 }} />
                    </IconWrapper>
                );
            default:
                return getChannelIcon(item?.channelType);
        }
    };

    const visibleActivities = contact?.activities?.filter(item => {
        const module = ACTIVITY_MODULE[item?.type];
        return !module || hasAccess(module);
    });

    return visibleActivities?.map(item => (
        <ActivityWrapper key={item.id}>
            {getActivityIcon(item)}
            {item?.type === CONTACT_ACTIVITY_TYPE.MessageSent && (
                <div>
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityMessageSent', {
                            channel: item?.channelName,
                        })}
                    </Typography>
                    <Tooltip title={item?.message} placement="top">
                        <span style={{ cursor: 'pointer' }}>
                            <Typography variant="caption" sx={{ color: 'var(--primary-main)', display: 'block' }}>
                                <ErrorOutlineRoundedIcon sx={{ fontSize: 12, marginRight: '4px' }} />
                                {t('buttons.viewMore')}
                            </Typography>
                        </span>
                    </Tooltip>
                </div>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.SurveyResponded && (
                <InternalLink to={`${RoutePath.SurveysResponses}?feedbackId=${item.feedbackId}`} underline="hover">
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activitySurveyResponded', {
                            template:
                                item?.surveyName ||
                                (item?.surveyResponseSource === SurveyResponseSource.RecoveryForm
                                    ? t('common.recoveryForm')
                                    : ''),
                            rating: item?.rating,
                        })}
                    </Typography>
                </InternalLink>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.SurveySent && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('ContactsPage.activitySurveySent', { template: item?.surveyName })}
                </Typography>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.SurveyReminderSent && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('ContactsPage.activitySurveyReminder', { template: item?.surveyName })}
                </Typography>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.ConversationStarted &&
                (item?.conversationId ? (
                    <InternalLink
                        to={`${RoutePath.MessagingInbox}?conversationId=${item?.conversationId}`}
                        onClick={onClickInboxActivity}
                        underline="hover"
                    >
                        <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                            {t('ContactsPage.activityConversationStarted')}
                        </Typography>
                    </InternalLink>
                ) : (
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityConversationStarted')}
                    </Typography>
                ))}
            {item?.type === CONTACT_ACTIVITY_TYPE.ConversationClosed &&
                (item?.conversationId ? (
                    <InternalLink
                        to={`${RoutePath.MessagingInbox}?conversationId=${item?.conversationId}`}
                        onClick={onClickInboxActivity}
                        underline="hover"
                    >
                        <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                            {t('ContactsPage.activityConversationClosed', {
                                name: getActorName(item) || item?.agentName,
                            })}
                        </Typography>
                    </InternalLink>
                ) : (
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityConversationClosed', {
                            name: getActorName(item) || item?.agentName,
                        })}
                    </Typography>
                ))}
            {item?.type === CONTACT_ACTIVITY_TYPE.ConversationReopened &&
                (item?.conversationId ? (
                    <InternalLink
                        to={`${RoutePath.MessagingInbox}?conversationId=${item?.conversationId}`}
                        onClick={onClickInboxActivity}
                        underline="hover"
                    >
                        <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                            {t('ContactsPage.activityConversationReopened', {
                                name: getActorName(item),
                            })}
                        </Typography>
                    </InternalLink>
                ) : (
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityConversationReopened', { name: getActorName(item) })}
                    </Typography>
                ))}
            {item?.type === CONTACT_ACTIVITY_TYPE.ContactCreated && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('common.contactCreated')}
                </Typography>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.ReviewLeft && (
                <InternalLink to={`${RoutePath.ReviewsManage}?reviewId=${item.reviewId}`} underline="hover">
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {t('ContactsPage.activityReviewLeft', {
                            reviewType: ProfileType[item?.reviewType],
                        })}
                    </Typography>
                </InternalLink>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.Integration && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('ContactsPage.activityIntegrationEvent', {
                        integration: ProfileType[item?.integrationType],
                    })}
                </Typography>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.CustomEvent && (
                <div>
                    <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                        {getCustomEventTitle(item)}
                    </Typography>
                    <Tooltip
                        title={
                            item?.customEventType === PAYMENT_COMPLETED_EVENT ? (
                                <PaymentDetails data={JSON.parse(item?.customEventDataRaw || '{}')} />
                            ) : (
                                <CustomEventDataRaw>
                                    {pretifyObject(JSON.parse(item?.customEventDataRaw || '{}'))}
                                </CustomEventDataRaw>
                            )
                        }
                        placement="top"
                    >
                        <span style={{ cursor: 'pointer' }}>
                            <Typography variant="caption" sx={{ color: 'var(--primary-main)', display: 'block' }}>
                                <ErrorOutlineRoundedIcon sx={{ fontSize: 12, marginRight: '4px' }} />
                                {t('buttons.viewMore')}
                            </Typography>
                        </span>
                    </Tooltip>
                </div>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.CouponRedeemed && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('CouponsPage.redeemed', { title: item?.couponTitle })}
                </Typography>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.FormSubmitted && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {t('ContactsPage.activityFormSubmitted', { name: item?.formName })}
                </Typography>
            )}
            {item?.type === CONTACT_ACTIVITY_TYPE.AddedToSegment && (
                <Typography variant={isSmallScreen && resizable ? 'caption' : 'body2'}>
                    {item?.labelName
                        ? t('ContactsPage.activityAddedToSegment', { name: item.labelName })
                        : t('AutomationsPage.triggers.addedToSegment')}
                </Typography>
            )}
            <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', display: 'block', textAlign: 'end' }}>
                {formatDateOrEmpty(item?.createdAt, 'd MMM yy', language)}
                <br />
                {formatDateOrEmpty(item?.createdAt, 'HH:mm')}
            </Typography>
        </ActivityWrapper>
    ));
};

export default Activity;
